import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Location } from '@freelancer/datastore/collections';
import { DurationFormat } from '@freelancer/pipes';
import { ModalService } from '@freelancer/ui';
import { FlagSize } from '@freelancer/ui/flag';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline, LinkWeight } from '@freelancer/ui/link';
import { ModalSize } from '@freelancer/ui/modal';
import { textVariants } from '@freelancer/ui/text';
import { TextSize } from '@freelancer/ui/text';
import { InvitationTypeApi } from 'api-typings/users/users';

@Component({
  standalone: false,
  selector: 'fl-experience-details-pattern',
  template: `
    <div class="Container">
      <div class="HeaderContainer">
        @if (subtitle) {
          <p
            [class]="cls"
            [class.mr-xxxsmall]="true"
            [class.mb-xxxsmall]="true"
            [class.font-medium]="true"
          >
            {{ subtitle }}
          </p>
        }
        @if (canEdit) {
          <fl-icon
            class="Icon"
            [name]="'ui-edit'"
            [color]="IconColor.INHERIT"
            [hoverAnimation]="'highlight'"
            [size]="IconSize.SMALL"
            [clickable]="true"
            [useIconFont]="true"
            (click)="triggerEdit()"
          ></fl-icon>
        }
      </div>
      @if (startDate) {
        <div class="DateRoleContainer mb-xxsmall">
          <p
            [class]="cls"
            [class.mr-xxsmall]="true"
            [class.mb-xxxsmall]="true"
          >
            {{ startDate | date: 'MMM, yyyy' }} -
            @if (endDate || endDate !== undefined) {
              {{ endDate | date: 'MMM, yyyy' }}
            } @else {
              <ng-container i18n="Present"> Present </ng-container>
            }
          </p>
          <p
            [class]="cls"
            [class.mr-xxsmall]="true"
          >
            {{ '•' }}
          </p>
          <p
            [class]="cls"
            [class.mr-xxsmall]="true"
            [class.mb-xxxsmall]="true"
          >
            {{ duration | duration: DurationFormat.LONG : startDate | flAsync }}
          </p>
        </div>
      }
      <p class="Description {{ cls }}">{{ description }}</p>
    </div>
    @if (location) {
      <div class="Location">
        <fl-flag
          class="mr-xxsmall"
          [countryCode]="location.country?.code"
          [sizeDesktop]="FlagSize.MID"
        ></fl-flag>
        <p
          [class]="cls"
          [class.mr-xxxsmall]="true"
        >
          {{ location.city ? location.city + ',' : '' }}
          {{ location.country?.name }}
        </p>
      </div>
    }
    @if (isViewerProfileOwner && showExternalReferenceRequestButton) {
      <fl-link
        class="Reference"
        i18n="Request a reference for an experience"
        flTrackingSection="Experience"
        flTrackingLabel="RequestReference"
        [color]="LinkColor.FOREGROUND"
        [display]="'block'"
        [size]="sizeDesktop ?? TextSize.BODY_16"
        [sizeDesktopXXLarge]="sizeDesktopXXLarge ?? TextSize.BODY_20"
        [underline]="LinkUnderline.ALWAYS"
        [weight]="LinkWeight.MEDIUM"
        (click)="handleRequestReferenceClick()"
      >
        <fl-icon
          class="mr-xxsmall"
          [name]="'ui-add-note'"
          [useIconFont]="true"
          [fillIconFont]="false"
          [size]="IconSize.SMALL"
          [sizeDesktopXXLarge]="IconSize.MID"
        >
        </fl-icon
        >Request a reference</fl-link
      >
    }

    <ng-content> </ng-content>
  `,
  styleUrls: ['./experience-details-pattern.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExperienceDetailsPatternComponent implements OnInit, OnChanges {
  private modalService = inject(ModalService);

  DurationFormat = DurationFormat;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  LinkWeight = LinkWeight;
  FlagSize = FlagSize;
  IconColor = IconColor;
  IconSize = IconSize;
  TextSize = TextSize;

  @Input() subtitle?: string;
  @Input() description?: string;
  @Input() startDate?: number;
  @Input() endDate?: number;
  @Input() duration?: number;
  @Input() location?: Location;
  @Input() canEdit: boolean;
  @Input() size = TextSize.BODY_16;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;
  @Input() isViewerProfileOwner?: boolean;
  @Input() experienceOwnerId: number;
  @Input() showExternalReferenceRequestButton: boolean;
  @Input() hasReferences: boolean;

  @Output() clickEdit = new EventEmitter<void>();

  cls: string;

  ngOnInit(): void {
    if (this.endDate === undefined && this.startDate !== undefined) {
      this.duration = Date.now() - this.startDate;
    }

    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'size' in changes ||
      'sizeDesktop' in changes ||
      'sizeDesktopXXLarge' in changes
    ) {
      this.computeClasses();
    }
  }

  triggerEdit(): void {
    this.clickEdit.emit();
  }

  handleRequestReferenceClick(): void {
    this.modalService.open('CreateInvitationModal', {
      inputs: {
        titleText: $localize`Request a reference`,
        description: $localize`Invite your past employers to write a message about working with you.`,
        errorMessage: $localize`Failed to invite user to write a reference.`,
        contextId: this.experienceOwnerId,
        invitationType: InvitationTypeApi.EXTERNAL_REFERENCE,
        enableCustomInviteMessage: true,
        disableLinkSharing: true,
        enableInvitationListAlwaysExpanded: true,
        maxInvites: 5,
        disableCloseOnInvite: true,
        inputPlaceholder: $localize`Enter email addresses`,
      },
      size: ModalSize.SMALL,
      mobileFullscreen: true,
      customViewHeader: true,
    });
  }

  private computeClasses(): void {
    this.cls = textVariants({
      size: this.size,
      sizeTablet: this.sizeDesktop,
      sizeDesktopXXL: this.sizeDesktopXXLarge,
    });
  }
}
